import axiosInstance from "./axiosConfig";
import {
  CLIENT_DOCUMENT_TYPES,
  MAX_CLIENT_DOCUMENT_BYTES
} from "./types/foreignNationalDocument";
import type {
  CaseDocumentKind,
  ChecklistResponse,
  ClientCaseDocument,
  ClientDocumentUploadSession,
  ForeignNationalDocument,
  ForeignNationalDocumentFilingStatus
} from "./types/foreignNationalDocument";
import { putToUploadSession } from "./utils/directUpload";
import type { UploadProgress } from "./utils/directUpload";
import { APIError, handleAPIError, handleBinaryAPIError } from "./utils/errors";

const BASE_URL = "/foreign_nationals";

/**
 * The type the file is stored as: by its extension, which browsers fill in
 * inconsistently for Office files, else by the type the browser declared.
 */
const contentTypeOf = (file: File): string | null => {
  const ext = file.name.includes(".")
    ? file.name.split(".").pop()?.toLowerCase() ?? ""
    : "";
  if (CLIENT_DOCUMENT_TYPES[ext]) return CLIENT_DOCUMENT_TYPES[ext];
  const declared = (file.type || "").toLowerCase();
  return new Set(Object.values(CLIENT_DOCUMENT_TYPES)).has(declared)
    ? declared
    : null;
};

const checkSize = (name: string, size: number) => {
  if (size <= 0) {
    throw new APIError(`${name} is empty.`, 400);
  }
  if (size > MAX_CLIENT_DOCUMENT_BYTES) {
    throw new APIError(
      `${name} is larger than ${MAX_CLIENT_DOCUMENT_BYTES / (1024 * 1024)} MB.`,
      413
    );
  }
};

/** Refuse what the API would, before any bytes move. */
const checkUploadable = (file: File): string => {
  const contentType = contentTypeOf(file);
  if (!contentType) {
    throw new APIError(
      `${file.name} isn't a PDF, image (JPG, PNG), Word, Excel or PowerPoint file, so it can't be added.`,
      415
    );
  }
  checkSize(file.name, file.size);
  return contentType;
};

const documentUrl = (foreignNationalId: string, documentId: string) =>
  `${BASE_URL}/${foreignNationalId}/documents/${encodeURIComponent(
    documentId
  )}`;

export const ForeignNationalDocumentsAPI = {
  async list(foreignNationalId: string): Promise<ForeignNationalDocument[]> {
    try {
      const response = await axiosInstance.get<ForeignNationalDocument[]>(
        `${BASE_URL}/${foreignNationalId}/documents`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Upload `file` straight to storage: open an upload session, PUT the bytes
   * to it (progress comes from this step), then have the API record the
   * document. The bytes never pass through the API, so its request cap does
   * not bound the file size.
   */
  async upload(
    foreignNationalId: string,
    file: File,
    opts?: {
      signal?: AbortSignal;
      onProgress?: (args: UploadProgress) => void;
    }
  ): Promise<ForeignNationalDocument> {
    try {
      const contentType = checkUploadable(file);
      const base = `${BASE_URL}/${foreignNationalId}/documents/uploads`;
      const { data: session } =
        await axiosInstance.post<ClientDocumentUploadSession>(
          base,
          {
            filename: file.name,
            content_type: contentType,
            size_bytes: file.size
          },
          { signal: opts?.signal }
        );
      await putToUploadSession(session.upload_url, file, {
        contentType: session.content_type ?? contentType,
        signal: opts?.signal,
        onProgress: (progress) => {
          try {
            opts?.onProgress?.(progress);
          } catch {
            // ignore progress errors
          }
        }
      });
      const { data } = await axiosInstance.post<ForeignNationalDocument>(
        `${base}/${encodeURIComponent(session.upload_id)}/complete`,
        undefined,
        { signal: opts?.signal }
      );
      return data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async remove(
    foreignNationalId: string,
    foreignNationalDocumentId: string
  ): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.delete<{ success: boolean }>(
        `${BASE_URL}/${foreignNationalId}/documents/${foreignNationalDocumentId}`
      );
      return response.data ?? { success: true };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateFilingStatus(
    foreignNationalId: string,
    foreignNationalDocumentId: string,
    filingStatus: ForeignNationalDocumentFilingStatus
  ): Promise<ForeignNationalDocument> {
    try {
      const response = await axiosInstance.patch<ForeignNationalDocument>(
        `${BASE_URL}/${foreignNationalId}/documents/${foreignNationalDocumentId}`,
        { filing_status: filingStatus }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },
  async setShared(
    foreignNationalId: string,
    foreignNationalDocumentId: string,
    shared: boolean
  ): Promise<ForeignNationalDocument> {
    try {
      const response = await axiosInstance.patch<ForeignNationalDocument>(
        `${BASE_URL}/${foreignNationalId}/documents/${foreignNationalDocumentId}`,
        { shared_with_client: shared }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Sets what a document is ("Master's Diploma", "W-2 (2025)"); the checklist files it by this. */
  async setLabel(
    foreignNationalId: string,
    foreignNationalDocumentId: string,
    label: string
  ): Promise<ForeignNationalDocument> {
    try {
      const response = await axiosInstance.patch<ForeignNationalDocument>(
        `${BASE_URL}/${foreignNationalId}/documents/${foreignNationalDocumentId}`,
        { label }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getChecklist(foreignNationalId: string): Promise<ChecklistResponse> {
    try {
      const response = await axiosInstance.get<ChecklistResponse>(
        `${BASE_URL}/${foreignNationalId}/documents/checklist`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listCaseDocuments(
    foreignNationalId: string
  ): Promise<{ documents: ClientCaseDocument[]; kinds: CaseDocumentKind[] }> {
    try {
      const response = await axiosInstance.get<{
        documents: ClientCaseDocument[];
        kinds: CaseDocumentKind[];
      }>(`${BASE_URL}/${foreignNationalId}/case_documents`);
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async saveCaseDocument(
    foreignNationalId: string,
    individualId: string,
    nodeId: string
  ): Promise<ForeignNationalDocument> {
    try {
      const response = await axiosInstance.post<ForeignNationalDocument>(
        `${BASE_URL}/${foreignNationalId}/case_documents/save`,
        { individual_id: individualId, node_id: nodeId }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getFile(
    foreignNationalId: string,
    foreignNationalDocumentId: string
  ): Promise<{ data: string; filePath: string; content_type: string }> {
    try {
      const response = await axiosInstance.get<{
        data: string;
        filePath: string;
        content_type: string;
      }>(
        `${BASE_URL}/${foreignNationalId}/documents/${foreignNationalDocumentId}/file`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * The document's bytes (`GET .../content`). Large files stream, so unlike
   * `getFile` this works for any size the vault accepts.
   */
  async getFileBlob(
    foreignNationalId: string,
    foreignNationalDocumentId: string,
    opts?: { download?: boolean; signal?: AbortSignal }
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `${BASE_URL}/${foreignNationalId}/documents/${foreignNationalDocumentId}/content`,
        {
          responseType: "blob",
          params: opts?.download ? { download: 1 } : undefined,
          signal: opts?.signal
        }
      );
      return response.data;
    } catch (error) {
      throw await handleBinaryAPIError(error);
    }
  },

  /**
   * The document as a PDF (`GET .../pdf`): a PDF as it is, anything else
   * converted by the server. Legacy Office previews and "Annotate as PDF"
   * on images read this.
   */
  async getFilePdfBlob(
    foreignNationalId: string,
    foreignNationalDocumentId: string,
    opts?: { signal?: AbortSignal }
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get<Blob>(
        `${documentUrl(foreignNationalId, foreignNationalDocumentId)}/pdf`,
        { responseType: "blob", signal: opts?.signal }
      );
      return response.data;
    } catch (error) {
      throw await handleBinaryAPIError(error);
    }
  },

  /**
   * Save an editor's bytes as the document's new current version, straight
   * to storage like `upload`. `baseChecksum` is the checksum the editor
   * loaded; when the document changed since, this throws an `APIError` 409
   * and nothing is saved.
   */
  async saveVersion(
    foreignNationalId: string,
    foreignNationalDocumentId: string,
    file: Blob,
    opts?: { note?: string | null; baseChecksum?: string | null }
  ): Promise<ForeignNationalDocument> {
    try {
      checkSize("The file", file.size);
      const base = `${documentUrl(
        foreignNationalId,
        foreignNationalDocumentId
      )}/versions/uploads`;
      const { data: session } =
        await axiosInstance.post<ClientDocumentUploadSession>(base, {
          size_bytes: file.size,
          base_checksum: opts?.baseChecksum ?? null
        });
      await putToUploadSession(session.upload_url, file, {
        contentType: session.content_type ?? file.type
      });
      const { data } = await axiosInstance.post<{
        document: ForeignNationalDocument;
      }>(`${base}/${encodeURIComponent(session.upload_id)}/complete`, {
        base_checksum: opts?.baseChecksum ?? null,
        note: opts?.note ?? null
      });
      return data.document;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /** Bring an earlier version back; the current bytes become a version too. */
  async restoreVersion(
    foreignNationalId: string,
    foreignNationalDocumentId: string,
    index: number
  ): Promise<ForeignNationalDocument> {
    try {
      const { data } = await axiosInstance.post<{
        document: ForeignNationalDocument;
      }>(
        `${documentUrl(
          foreignNationalId,
          foreignNationalDocumentId
        )}/restore-version`,
        { index }
      );
      return data.document;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  async sendToCase(
    foreignNationalId: string,
    payload: {
      individual_id: string;
      case_id: string;
      foreign_national_document_ids: string[];
    }
  ): Promise<{
    created_document_ids: string[];
    updated_foreign_national_document_ids: string[];
  }> {
    try {
      const response = await axiosInstance.post<{
        created_document_ids: string[];
        updated_foreign_national_document_ids: string[];
      }>(`${BASE_URL}/${foreignNationalId}/documents/send-to-case`, payload);
      return (
        response.data ?? {
          created_document_ids: [],
          updated_foreign_national_document_ids: []
        }
      );
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
